import {Trans} from '@lingui/macro';
import {uniq} from 'lodash';
import React, {FC, useState} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownItemIcon} from '../../../../components/common/dropdowns/dropdownItemIcon';
import {MultipleGroupsDropdownBox} from '../dropdowns/multipleGroupsDropdownBox';
import {DropdownGroup} from '../dropdowns/multipleGroupsDropdownHelpers';
import {TokensEditorInput} from '../inputs/tokensEditorInput';
import {FileExtensionParameterErrorsEnum} from './fileExtensionParameterErrors';
import {defaultDropdownWidth, GenericMultipleParameterProps} from './genericParameter';

/*
 * Types.
 */

interface FileExtensionParameterProps
  extends Omit<
    GenericMultipleParameterProps<{id: number}>,
    'selectedItems' | 'onChange' | 'canSelectAnyItem'
  > {
  extensions: ReadonlyArray<string>;
  onChange: HandlerOf<ReadonlyArray<string>>;
}

/*
 * Constants.
 */

const presets = [
  ['pdf'],
  ['doc', 'docx'],
  ['ppt', 'pptx'],
  ['xls', 'xlsx'],
  ['csv'],
  ['jpg', 'jpeg', 'png'],
  ['xml'],
  ['txt'],
  ['zip', 'gzip'],
];

const formatRegexp = /^(?:\.?\w+)+$/;
const delimiterRegexp = /[,;\t\n\s]+/;

const maxExtensionLength = 10;
const maxExtensionCount = 15;

/*
 * Component.
 */

export const FileExtensionParameter: FC<FileExtensionParameterProps> = ({extensions, onChange, ...rest}) => {
  const [editorInput, setEditorInput] = useState('');
  const theme = useUserTheme();

  // A preset is selected if all its elements are in the input extensions array.
  const selectedPresets = presets.filter((preset) =>
    preset.every((extension) => extensions.includes(extension)),
  );

  // All extensions that are not part of a selected preset.
  const [extraExtensions, setExtraExtensions] = useState(pruneExtensions(extensions, selectedPresets));
  const [displayAddExtensions, setDisplayAddExtensions] = useState(extraExtensions.length === 0);

  // Items must be an array of arrays, so we wrap individual extensions.
  const extraGroupItems = pruneExtensions(extraExtensions, selectedPresets).map((item) => [item]);

  // We select all items in the extra group in order to render them in the dropdown button.
  const selectedItems = [...selectedPresets, ...extraGroupItems];

  const presetsGroup = {
    items: presets,
    getItemKey: serializeExtensions,
    renderItemLabel: serializeExtensions,
    serializeItemForSearch: serializeExtensions,
  };

  const onUpdate = (selected: ReadonlyArray<string>) => {
    // Try to validate current input.
    const inputToken = editorInput && !validateInput(editorInput) && sanitizeToken(editorInput);
    const newSelectedExtensions = inputToken ? uniq([...selected, inputToken]) : selected;
    // Recalculate which presets should now be selected.
    const newSelectedPresets = presets.filter((preset) =>
      preset.every((extension) => newSelectedExtensions.includes(extension)),
    );
    const prunedExtraExtensions = pruneExtensions(newSelectedExtensions, newSelectedPresets);

    // Reset if input was added.
    setEditorInput((previous) => (inputToken ? '' : previous));
    setExtraExtensions(prunedExtraExtensions);
    onChange([...newSelectedPresets.flat(), ...prunedExtraExtensions]);
  };

  const onToggle = (isOpen: boolean) => {
    // Cleanup on open (we don't want to clean up during user input).
    if (isOpen) {
      setExtraExtensions(pruneExtensions(extraExtensions, selectedPresets));
    }
    // Save on close.
    else {
      onUpdate([...selectedPresets.flat(), ...extraExtensions]);
    }
  };

  const validateInput = (input: string): FileExtensionParameterErrorsEnum | undefined => {
    if (extraExtensions.length >= maxExtensionCount) {
      return FileExtensionParameterErrorsEnum.TOKENS_COUNT;
    }

    const sanitized = sanitizeToken(input);

    if (sanitized.length > maxExtensionLength) {
      return FileExtensionParameterErrorsEnum.TOKEN_LENGTH;
    }

    // Check following errors only at validation attempt.
    if (!input.slice(-1).match(delimiterRegexp)) {
      return undefined;
    }

    if (extraExtensions.includes(sanitized)) {
      return FileExtensionParameterErrorsEnum.DUPLICATE_TOKEN;
    }

    if (!sanitized.match(formatRegexp)) {
      return FileExtensionParameterErrorsEnum.INCORRECT_FORMAT;
    }

    return undefined;
  };

  const onTokensChange = (tokens: ReadonlyArray<string>) => {
    const sanitizedTokens = uniq(tokens.map(sanitizeToken).slice(0, maxExtensionCount));
    const prunedTokens = pruneExtensions(sanitizedTokens, selectedPresets);

    setExtraExtensions(sanitizedTokens);
    onChange([...selectedPresets.flat(), ...prunedTokens]);
  };

  const renderExtraExtensionsGroup = () => (
    <TokensEditorInput
      tokens={extraExtensions}
      input={editorInput}
      delimiters={delimiterRegexp}
      validateInput={validateInput}
      setInput={setEditorInput}
      onTokensChange={onTokensChange}
    />
  );

  const extraExtensionsGroup = {
    items: extraGroupItems.length > 0 ? extraGroupItems : [[]],
    title: `Other extensions (${extraExtensions.length})`,
    isInteractive: false,
    getItemKey: getFirstExtension,
    renderItemLabel: getFirstExtension,
    serializeItemForSearch: getFirstExtension,
    render: renderExtraExtensionsGroup,
  };

  const renderAddExtensionsGroup = () => (
    <DropdownItem onSelect={() => setDisplayAddExtensions(false)}>
      <DropdownItemIcon
        iconSize={16}
        iconName="plusCircleSmall"
        color={theme.palette.blue.shade40}
        label="Add"
      />
      <DropdownItemContent>
        <Trans id="file-extension-parameter-add-extension">Add an extension</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );

  const addExtensionGroup = {
    items: [[]],
    getItemKey: getFirstExtension,
    renderItemLabel: getFirstExtension,
    serializeItemForSearch: getFirstExtension,
    render: renderAddExtensionsGroup,
  };

  const groups: ReadonlyArray<DropdownGroup<ReadonlyArray<string>>> = displayAddExtensions
    ? [presetsGroup, addExtensionGroup]
    : [presetsGroup, extraExtensionsGroup];

  return (
    <MultipleGroupsDropdownBox<ReadonlyArray<string>>
      {...rest}
      groups={groups}
      selectedItems={selectedItems}
      onChange={(arrays) => onUpdate(arrays.flat())}
      onToggle={onToggle}
      width={defaultDropdownWidth}
      placeholder={<Trans id="rule-editor-file-extension-param-placeholder">Select extensions</Trans>}
      isSearchDisabled
      canSelectMultipleItems
    />
  );
};

/*
 * Helpers.
 */

function sanitizeToken(token: string) {
  // Removes leading and closing punctuation but keeps inner one (ex: tar.gz)
  return token.replace(/^\W+/, '').replace(/\W+$/, '').toLowerCase();
}

function getFirstExtension(extensions: ReadonlyArray<string>) {
  return extensions[0] ?? '';
}

function serializeExtensions(extensions: ReadonlyArray<string>) {
  return extensions.join('/');
}

function pruneExtensions(
  extensions: ReadonlyArray<string>,
  selectedPresets: ReadonlyArray<ReadonlyArray<string>>,
) {
  const selectedPresetExtensions = selectedPresets.flat();
  return extensions.filter((extension) => !selectedPresetExtensions.includes(extension));
}
